// SignaturPage — Online-Unterschrift der Modulliste (Bearbeitungsbestätigung)
// Erreichbar über E-Mail-Link /signatur/:courseId nach "Kurs beendet" durch die Akademie.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardSignature, Download, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LERNARTEN = [
  { value: 'kurs', label: 'Im Kurs bearbeitet', desc: 'Ich habe die Kapitel im Lernportal durchgearbeitet.' },
  { value: 'selbst', label: 'Selbstständig gelernt', desc: 'Ich habe mir die Inhalte selbstständig angeeignet.' },
  { value: 'anderweitig', label: 'Anderweitig erworben', desc: 'Ich habe die Inhalte z. B. durch Berufspraxis oder frühere Fortbildungen erworben.' },
];

export default function SignaturPage({ Layout }) {
  const { courseId } = useParams();
  const { getAuthHeader } = useAuth();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState('kurs');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canvasRef = useRef(null);

  const loadStatus = () => axios.get(`${API}/user/modulliste/${courseId}/status`, getAuthHeader())
    .then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/courses/${courseId}`, getAuthHeader()),
      axios.get(`${API}/courses/${courseId}/modules`, getAuthHeader()),
    ]).then(([c, m]) => { setCourse(c.data); setModules(m.data || []); }).catch(() => toast.error('Kurs konnte nicht geladen werden'));
    loadStatus();
  }, [courseId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#211812'; ctx.lineWidth = 2.2; ctx.lineCap = 'round';
    let drawing = false;
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: ((p.clientX - r.left) / r.width) * canvas.width, y: ((p.clientY - r.top) / r.height) * canvas.height };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing = false; };
    canvas.addEventListener('mousedown', start); canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start); canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start); canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [course, status?.signed]);

  const clear = () => {
    const c = canvasRef.current;
    if (c) { c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasDrawn(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/modulliste/${courseId}/sign`,
        { signature_data: canvasRef.current.toDataURL('image/png'), learning_mode: mode }, getAuthHeader());
      toast.success('Unterschrift gespeichert — vielen Dank!');
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    setSubmitting(false);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/user/modulliste/${courseId}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Bearbeitungsbestaetigung.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Fehler beim Download'); }
    setDownloading(false);
  };

  const body = !course ? (
    <div className="py-24 text-center text-sand-500">Lade…</div>
  ) : (
    <div className="max-w-3xl mx-auto px-5 py-10" data-testid="signatur-page">
      <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.24em] text-terra">Bearbeitungsbestätigung</p>
      <h1 className="font-display text-3xl sm:text-4xl text-espresso mt-1">Online unterschreiben</h1>
      <p className="text-[14px] text-sand-600 mt-2">{course.title}</p>

      {status?.signed && (
        <div className="mt-6 rounded-xl border px-5 py-4 flex flex-wrap items-center gap-3" style={{ borderColor: '#BFD8C2', backgroundColor: '#F2F8F3' }} data-testid="signatur-done">
          <CheckCircle2 className="w-5 h-5" style={{ color: '#2F7A3D' }} />
          <p className="text-[13.5px] flex-1" style={{ color: '#2F7A3D' }}>
            Unterschrieben am {new Date(status.signed_at).toLocaleDateString('de-DE')} — deine Bestätigung ist bei der Akademie hinterlegt.
          </p>
          <button onClick={download} disabled={downloading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50"
            style={{ backgroundColor: '#E07A5F' }} data-testid="signatur-download">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
          </button>
        </div>
      )}

      <div className="mt-8">
        <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.22em] text-sand-500 mb-3">Kapitel dieser Weiterbildung ({modules.length})</p>
        <div className="rounded-xl border border-cream-300 bg-cream-50 divide-y divide-cream-200 max-h-[340px] overflow-y-auto" data-testid="signatur-kapitel-liste">
          {modules.map((m) => (
            <div key={m.id} className="px-4 py-2 flex items-baseline gap-3">
              <span className="text-[11px] text-sand-400 w-6 flex-shrink-0 text-right">{m.order}</span>
              <span className="text-[13px] text-espresso">{m.title}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-cream-300 bg-cream-100 p-5">
        <p className="text-[14px] text-espresso leading-relaxed">
          „Hiermit bestätige ich, alle Kapitel vollständig bearbeitet bzw. mir die Inhalte selbstständig
          oder anderweitig angeeignet zu haben."
        </p>

        <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.22em] text-sand-500 mt-5 mb-2">Lernart wählen</p>
        <div className="space-y-2">
          {LERNARTEN.map((l) => (
            <label key={l.value} className="flex items-start gap-3 cursor-pointer rounded-lg px-3 py-2 border transition-colors"
              style={mode === l.value ? { borderColor: '#E07A5F', backgroundColor: '#FDF1EC' } : { borderColor: '#E5DCCF' }}
              data-testid={`signatur-lernart-${l.value}`}>
              <input type="radio" name="lernart" checked={mode === l.value} onChange={() => setMode(l.value)} className="mt-1 accent-[#E07A5F]" />
              <span>
                <span className="block text-[13.5px] font-semibold text-espresso">{l.label}</span>
                <span className="block text-[12px] text-sand-600">{l.desc}</span>
              </span>
            </label>
          ))}
        </div>

        <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.22em] text-sand-500 mt-6 mb-2">Deine Unterschrift (mit Finger oder Maus)</p>
        <canvas ref={canvasRef} width={640} height={190}
          className="w-full bg-white border-2 border-dashed border-cream-400 rounded-xl touch-none"
          data-testid="signatur-canvas" />
        <div className="mt-4 flex items-center justify-between">
          <button onClick={clear} className="text-[13px] text-sand-600 underline" data-testid="signatur-clear">Löschen</button>
          <button onClick={submit} disabled={!hasDrawn || submitting}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-50"
            style={{ backgroundColor: '#E07A5F' }} data-testid="signatur-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardSignature className="w-3.5 h-3.5" />}
            {status?.signed ? 'Neu unterschreiben' : 'Unterschrift einreichen'}
          </button>
        </div>
      </div>
    </div>
  );

  return Layout ? <Layout>{body}</Layout> : body;
}
